import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})

export class GoalService {

  /*****************************************
   * 🎯 MONTHLY GOALS
   *****************************************/
  private monthlyGoals: { [key: string]: number } = {};

  setMonthlyGoal(year: number, month: number, goal: number): void {
    const key = this._key(year, month);
    this.monthlyGoals[key] = goal;
  }

  getMonthlyGoal(year: number, month: number): number {
    const key = this._key(year, month);
    return this.monthlyGoals[key] || 0;
  }

  /*****************************************
   * 📉 MONTHLY DELTAS (deficit/surplus)
   *****************************************/
  private monthlyDeltas: { [key: string]: number } = {};

  setMonthlyDelta(year: number, month: number, delta: number): void {
    const key = this._key(year, month);
    this.monthlyDeltas[key] = delta;
  }

  getMonthlyDelta(year: number, month: number): number {
    const key = this._key(year, month);
    return this.monthlyDeltas[key] || 0;
  }

  /*****************************************
   * 🗓️ WORKING DAYS PER MONTH
   *****************************************/
  private workingDaysByMonth: { [key: string]: number } = {};

  setWorkingDays(year: number, month: number, days: number): void {
    const key = `${year}-${month}`;
    this.workingDaysByMonth[key] = days;
  }

  getWorkingDays(year: number, month: number): number {
    const key = `${year}-${month}`;
    return this.workingDaysByMonth[key] ?? 21; // Default to 21 if not set
  }

  /*****************************************
   * 📊 MONTHLY PROGRESS HISTORY
   *****************************************/
  private goalHistory: {
    [key: string]: {
      total: number;
      completed: number;
    };
  } = {};

  setMonthlyProgress(year: number, month: number, total: number, completed: number): void {
    const key = `${year}-${month}`;
    this.goalHistory[key] = { total, completed };
  }

  getCompletedForMonth(year: number, month: number): number {
    const key = `${year}-${month}`;
    return this.goalHistory[key]?.completed || 0;
  }

  /*****************************************
   * ⚙️ CALCULATIONS
   *****************************************/

  /* Returns how much is still missing to reach the goal */
  getMonthlyDeficit(year: number, month: number): number {
    const key = `${year}-${month}`;
    const info = this.goalHistory[key];
    if (!info) return 0;

    const diff = info.total - info.completed;
    return diff > 0 ? diff : 0;
  }

  /* Calculates a dynamic daily goal based on progress so far and remaining working days */
  calculateDynamicDailyGoal(year: number, month: number, currentDay: number): number {
    const key = `${year}-${month}`;
    const info = this.goalHistory[key];

    if (!info) {
      console.warn("📛 No data found for the specified month");
      return 0;
    }

    const totalDays = this.getWorkingDays(year, month);
    const remainingDays = totalDays - currentDay;
    const remainingValue = info.total - info.completed;

    const newGoal = remainingDays > 0
      ? remainingValue / remainingDays
      : remainingValue;

    return Math.max(0, Math.round(newGoal));
  }

  /*****************************************
   * 🔄 GOAL STATUS TRACKING
   *****************************************/
  private goalReachedSource = new BehaviorSubject<boolean>(false);
  goalReached$ = this.goalReachedSource.asObservable();

  reachGoal(): void {
    this.goalReachedSource.next(true);
  }

  resetGoal(): void {
    this.goalReachedSource.next(false);
  }

  /*****************************************
   * 🧰 PRIVATE UTILITY
   *****************************************/
  private _key(year: number, month: number): string {
    return `${year}-${month.toString().padStart(2, '0')}`;
  }

}
